import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FormAttachmentUploadRequirements } from '../attachments/form-attachment-requirements';
import { GeneralFormData } from '../general-form-data.model';
import { FormStep } from '../form-steps.interface';
import { parseLocalDate } from '../../../utils/date-time-formatter';


export class EnergizedElecWorkPermit {
    id?: string;
    submittedByName: string = '';
    submittedByEmail: string = '';
    submissionDate?: Date;

    // Basic Job Information
    jobWorkOrderName: string = '';
    jobWorkOrderNumber: string = '';

    // Part 1: To Be Completed By The Requester
    circuitEquipmentDescription: string = '';
    upstreamOverCurrentDeviceLocation: string = '';
    workDescription: string = '';
    justificationForEnergizedWork: string = '';
    requesterTitle: string = '';
    requesterDate: Date | null = null;

    // Part 2: Hazards on the Job
    electricalHazards: string = '';
    mechanicalHazards: string = '';
    workingSpaceConstraints: string = '';
    otherHazards: string = '';

    // Part 3: To Be Completed By the Electrically Qualified Person Doing the Work
    detailedJobDescriptionProcedure: boolean = false;
    safeWorkPracticesDescription: boolean = false;
    shockHazardAnalysisDescription: boolean = false;
    shockProtectionBoundariesDetermination: boolean = false;
    arcFlashHazardAnalysisResults: boolean = false;
    arcFlashProtectionBoundaryDetermination: boolean = false;
    necessaryPPEForTask: boolean = false;
    unqualifiedPersonsAccessRestriction: boolean = false;
    jobBriefingCompletionEvidence: boolean = false;
    agreeWorkCanBeDoneSafely: string = '';

    // Part 4: Personal Protective Equipment
    safetyGlasses: boolean = false;
    insulatedTools: boolean = false;
    eightCalBalaclava: boolean = false;
    eightCalRubberGloves: boolean = false;
    eightCalFaceShield: boolean = false;
    eightCalFireResistanceClothing: boolean = false;
    eightCalFlashSuit: boolean = false;
    fortyCalRubberGloves: boolean = false;
    fortyCalFaceShield: boolean = false;
    fortyCalFlashSuit: boolean = false;
    otherPPE: boolean = false;
    otherPPEDescription: string = '';

    // Approval(s) to Perform the Work While Electrically Energized
    electricianName: string = '';
    electricianSignature: Array<string> = [];
    electricianSignatureDate: Date | null = null;
    supervisorName: string = '';
    supervisorSignature: Array<string> = [];
    supervisorSignatureDate: Date | null = null;

    constructor(data?: Partial<EnergizedElecWorkPermit>) {
        // Set default date to today if not provided
        if (data) {
            Object.assign(this, {
                ...data,
                submissionDate: data.submissionDate ? new Date(data.submissionDate) : null,
                requesterDate: data.requesterDate ? parseLocalDate(data.requesterDate as any) : null,
                electricianSignatureDate: data.electricianSignatureDate ? parseLocalDate(data.electricianSignatureDate as any) : null,
                supervisorSignatureDate: data.supervisorSignatureDate ? parseLocalDate(data.supervisorSignatureDate as any) : null
            });
        }
    }

    static getFormSteps(): FormStep[] {
        return [
            {
                id: 'basic',
                shortTitle: 'Basic Info',
                title: 'Basic Information',
                icon: 'person-outline',
                description: 'Enter the basic job information',
                sections: [
                    {
                        fields: ['jobWorkOrderName', 'jobWorkOrderNumber']
                    }
                ]
            },
            {
                id: 'requester',
                shortTitle: 'Requester Info',
                title: 'To Be Completed By The Requester',
                icon: 'document-text-outline',
                description: 'Enter the requester information and work details',
                sections: [
                    {
                        fields: ['circuitEquipmentDescription', 'upstreamOverCurrentDeviceLocation', 'workDescription', 'justificationForEnergizedWork', 'requesterTitle', 'requesterDate']
                    }
                ]
            },
            {
                id: 'hazards',
                shortTitle: 'Hazards',
                title: 'Hazards on the Job',
                icon: 'warning-outline',
                description: 'Document all potential hazards',
                sections: [
                    {
                        fields: ['electricalHazards', 'mechanicalHazards', 'workingSpaceConstraints', 'otherHazards']
                    }
                ]
            },
            {
                id: 'qualified-person',
                shortTitle: 'Qualified Person',
                title: 'To Be Completed By the Electrically Qualified Person',
                icon: 'shield-checkmark-outline',
                description: 'Complete the safety checklist',
                sections: [
                    
                    {
                        checkboxGroups: [
                            {
                                fields: ['detailedJobDescriptionProcedure', 'safeWorkPracticesDescription', 'shockHazardAnalysisDescription', 'shockProtectionBoundariesDetermination', 'arcFlashHazardAnalysisResults', 'arcFlashProtectionBoundaryDetermination', 'necessaryPPEForTask', 'unqualifiedPersonsAccessRestriction', 'jobBriefingCompletionEvidence'],
                                checkboxLabel: this.getFieldMappings()['checkWhenComplete']
                            }
                        ],
                        fields:['agreeWorkCanBeDoneSafely']
                    }
                ]
            },
            {
                id: 'ppe',
                shortTitle: 'PPE',
                title: 'Personal Protective Equipment',
                icon: 'shield-outline',
                description: 'Select required PPE',
                sections: [
                    {
                        checkboxGroups: [
                            {
                                fields: ['safetyGlasses', 'insulatedTools', 'eightCalBalaclava', 'eightCalRubberGloves', 'eightCalFaceShield', 'eightCalFireResistanceClothing', 'eightCalFlashSuit', 'fortyCalRubberGloves', 'fortyCalFaceShield', 'fortyCalFlashSuit', 'otherPPE'],
                                checkboxLabel: this.getFieldMappings()['ppeCheckboxes']
                            },

                        ],
                        fields:['otherPPEDescription']
                    }
                ]
            },
            {
                id: 'approvals',
                shortTitle: 'Approvals',
                title: 'Approvals',
                icon: 'checkmark-outline',
                description: 'Approve the work',
                sections: [
                    {
                            fields: ['electricianName', 'electricianSignature', 'electricianSignatureDate', 'supervisorName', 'supervisorSignature', 'supervisorSignatureDate']
                    }
                ]
            }
        ];
    }

    static getFormInfo(): GeneralFormData {
        return {
            formType: 'energized-elec-work-permit',
            formTitle: 'Energized Electrical Work Permit',
            formIcon: 'flash-outline',
            cardImage: 'assets/form-card-tiles/energized-elec-work-permit.png',
            formAttachmentRequirements: {
                signatures: [
                    {
                        identifier: 'electricianSignature',
                        label: this.getFieldMappings()['electricianSignature'],
                        required: false,
                      },
                    {
                      identifier: 'supervisorSignature',
                      label: this.getFieldMappings()['supervisorSignature'],
                      required: false,
                    }
                  ],
            }
        };
    }

    static getFieldMappings(): Record<string, string> {
        return {
            // Basic Information
            submittedByName: "Submitter Name",
            submittedByEmail: "Submitter Email",
            submissionDate: "Submission Date",
            
            // Basic Job Information
            jobWorkOrderName: "Job/Work Order Name",
            jobWorkOrderNumber: "Job/Work Order Number",

            // Part 1: To Be Completed By The Requester
            circuitEquipmentDescription: "Description of Circuit/Equipment or Tag#/Work Area",
            upstreamOverCurrentDeviceLocation: "Location of Upstream Over Current Device",
            workDescription: "Description of Work to be Done",
            justificationForEnergizedWork: "Justification of Why the Circuit/Equipment Cannot be De-Energized or the Work Deferred Unit the Next Scheduled Outage",
            requesterTitle: "Requester/Title",
            requesterDate: "Requester Date",

            // Part 2: Hazards on the Job
            electricalHazards: "Electrical Hazards",
            mechanicalHazards: "Mechanical Hazards",
            workingSpaceConstraints: "Working Space Constraints (Including Possible Obstructions in the Work Area)",
            otherHazards: "Other Hazards",

            // Part 3: To Be Completed By the Electrically Qualified Person Doing the Work
            checkWhenComplete: "Check When Complete",
            detailedJobDescriptionProcedure: "Detailed Job Description Procedure to Be Used in Performing the Above Detailed Work",
            safeWorkPracticesDescription: "Description of Safe Work Practices to Be Employed",
            shockHazardAnalysisDescription: "Description of the Shock Hazard Analysis",
            shockProtectionBoundariesDetermination: "Determination of Shock Protection Boundaries",
            arcFlashHazardAnalysisResults: "Results of the Arc Flash Hazard Analysis",
            arcFlashProtectionBoundaryDetermination: "Determination of the Arc Flash Protection Boundary",
            necessaryPPEForTask: "Necessary Personal Protective Equipment of Safely Performing the Assigned Task",
            unqualifiedPersonsAccessRestriction: "Means Employed to Restrict the Access of Unqualified Persons from the Work Area",
            jobBriefingCompletionEvidence: "Evidence of Completion of Job Briefing Including Discussion of Any Job Related Hazards",
            agreeWorkCanBeDoneSafely: "Do you Agree the Above Described Can Be Done Safely?",

            // Part 4: Personal Protective Equipment
            ppeCheckboxes: "Equipment",
            safetyGlasses: "Safety Glasses",
            insulatedTools: "Insulated Tools",
            eightCalBalaclava: "8 Cal Balaclava",
            eightCalRubberGloves: "8 Cal Rubber Gloves (w/Leather Outers)",
            eightCalFaceShield: "8 Cal Face Shield",
            eightCalFireResistanceClothing: "8 Cal Fire Resistance Clothing",
            eightCalFlashSuit: "8 Cal Flash Suit",
            fortyCalRubberGloves: "40 Cal Rubber Gloves (w/Leather Outers)",
            fortyCalFaceShield: "40 Cal Face Shield",
            fortyCalFlashSuit: "40 Cal Flash Suit",
            otherPPE: "Other PPE",
            otherPPEDescription: "Other PPE Description",

            // Approval(s) to Perform the Work While Electrically Energized
            electricianName: "Electrician",
            electricianSignature: "Electrician Signature",
            electricianSignatureDate: "Electrician Signature Date",
            supervisorName: "Supervisor",
            supervisorSignature: "Supervisor Signature",
            supervisorSignatureDate: "Supervisor Signature Date"
        };
    }
} 